import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FormularioCampo } from '@/types/formulario';
import { GripVertical, Settings, Trash2, Type, Hash, Calendar, Clock, CheckSquare, Circle, List, AlignLeft, FileUp, PenTool, Camera, MapPin, TrendingUp, ClipboardList } from 'lucide-react';

interface FormularioFieldItemProps {
  campo: FormularioCampo;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: () => void;
}

const fieldIcons = {
  texto: Type,
  numero: Hash,
  data: Calendar,
  hora: Clock,
  checkbox: CheckSquare,
  radio: Circle,
  select: List,
  textarea: AlignLeft,
  escala: TrendingUp,
  arquivo: FileUp,
  assinatura: PenTool,
  foto: Camera,
  localizacao: MapPin,
};

export const FormularioFieldItem = ({ campo, isSelected, onSelect, onDelete }: FormularioFieldItemProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: campo.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const Icon = fieldIcons[campo.tipo_campo];

  return (
    <Card
      ref={setNodeRef}
      style={style}
      className={`p-3 ${isSelected ? 'ring-2 ring-primary' : ''}`}
    >
      <div className="flex items-center gap-2">
        <button
          className="cursor-grab active:cursor-grabbing"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-5 w-5 text-muted-foreground" />
        </button>

        <Icon className="h-4 w-4 text-muted-foreground" />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium truncate">{campo.label}</span>
            {campo.obrigatorio && (
              <Badge variant="secondary" className="text-xs">Obrigatório</Badge>
            )}
            {campo.configuracoes?.plano_acao?.habilitado && (
              <Badge variant="outline" className="text-xs border-orange-500 text-orange-600 gap-1">
                <ClipboardList className="h-3 w-3" />
                Plano de Ação
              </Badge>
            )}
          </div>
          {campo.descricao && (
            <p className="text-xs text-muted-foreground truncate">{campo.descricao}</p>
          )}
        </div>

        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={onSelect}
          >
            <Settings className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={onDelete}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </Card>
  );
};
